import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Flex, VStack, HStack, Text, Heading, Button, Progress, Spinner, Alert, AlertIcon,
  FormControl, FormLabel, Input, SimpleGrid, Divider, Checkbox, useToast, Stack, Badge, Textarea, Select,
  Image, Icon,
} from '@chakra-ui/react';
import { FaIdCard, FaFileSignature, FaMapMarkedAlt, FaCalendarAlt, FaUserCheck, FaRoute, FaHandshake, FaCertificate, FaShieldAlt, FaKey } from 'react-icons/fa';
import SignatureCanvas from 'react-signature-canvas';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import W9Form, { isW9Complete } from './W9Form';
import ServiceZipPicker from './ServiceZipPicker';
import CredentialsUpload, { isCredentialsComplete } from './CredentialsUpload';
import { fillW9Pdf } from '../../utils/fillW9Pdf';

function dataUrlToBytes(dataUrl) {
  if (!dataUrl) return null;
  const base64 = dataUrl.split(',')[1] || '';
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const STEPS = [
  { id: 'welcome',     title: 'Welcome' },
  { id: 'contact',     title: 'Contact & Company' },
  { id: 'credentials', title: 'Credentials' },
  { id: 'w9',          title: 'IRS Form W-9' },
  { id: 'agreement',   title: 'Service Agreement' },
  { id: 'areas',       title: 'Service Areas' },
  { id: 'hours',       title: 'Availability' },
  { id: 'password',    title: 'Set Password' },
  { id: 'done',        title: 'Done' },
];

const DEFAULT_AGREEMENT = `PHLEBOTOMIST SERVICE AGREEMENT

This Phlebotomist Service Agreement ("Agreement") is made and entered into as of {EFFECTIVE_DATE} by and between At Home Blood Draw Inc ("Company") and the undersigned Phlebotomist ("Phlebotomist").

1. Services
Phlebotomist, as an independent contractor, shall collect patient specimens in accordance with valid physician orders and deliver them to the designated laboratory.

2. Compensation
Fee: {PER_DRAW_FEE} per specimen collected. Payment weekly via ACH.

3. Compliance & Conduct
Phlebotomist will comply with all applicable federal and state laws, including Anti-Kickback, Stark Law, HIPAA, and state licensing requirements.

4. Independent Contractor
Phlebotomist operates as an independent contractor and is responsible for taxes, insurance, licensure, and business expenses.

5. Confidentiality & Records
Phlebotomist shall protect Protected Health Information ("PHI") per HIPAA, governed by a separate Business Associate Agreement ("BAA").

6. Indemnification & Insurance
Phlebotomist shall maintain professional liability insurance of at least $1,000,000 per occurrence / $3,000,000 aggregate.

7. Termination
This Agreement continues until terminated by either party with 30 days' written notice.`;

export default function PhlebotomistOnboarding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [phleb, setPhleb] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Step state
  const [contact, setContact] = useState({ full_name: '', email: '', phone: '', company_name: '', company_address: '' });
  const [w9, setW9] = useState({});
  const [agreement, setAgreement] = useState({
    company_name: '',
    printed_name: '',
    effective_date: new Date().toISOString().split('T')[0],
    sign_date: new Date().toISOString().split('T')[0],
    per_draw_fee: '',
    bank_account_name: '',
    bank_name: '',
    bank_routing_number: '',
    bank_account_number: '',
    bank_account_type: 'checking',
    compliance_acknowledged: false,
    signature: null,
  });
  const agreementSigPad = useRef();
  const [zips, setZips] = useState([]);
  const [credentials, setCredentials] = useState({ drivers_license: null, phlebotomy_cert: null });
  const [hours, setHours] = useState(
    DAYS.map((_, i) => ({ day_of_week: i, available: i >= 1 && i <= 5, start_time: '09:00', end_time: '17:00' }))
  );
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from('phlebotomist_profiles')
          .select('*')
          .eq('onboarding_token', token)
          .maybeSingle();
        if (err) throw err;
        if (!data) throw new Error('This onboarding link is not valid.');
        if (cancelled) return;
        setPhleb(data);
        setContact({
          full_name: data.full_name || '',
          email: data.email || '',
          phone: data.phone || '',
          company_name: data.company_name || '',
          company_address: data.company_address || '',
        });
        // per_draw_fee in the wizard shows the PHLEB's earnings (phleb_pay_rate),
        // not the lab's bill rate (lab_draw_fee). Falls back to lab_draw_fee if
        // phleb_pay_rate hasn't been set yet (older profiles before the weekly
        // billing migration backfilled per-phleb rates).
        setAgreement((a) => ({
          ...a,
          company_name: data.company_name || '',
          printed_name: data.full_name || '',
          per_draw_fee: data.phleb_pay_rate != null
            ? String(data.phleb_pay_rate)
            : (data.lab_draw_fee != null ? String(data.lab_draw_fee) : ''),
          bank_account_name: data.full_name || '',
        }));
        setW9((w) => ({ ...w, legal_name: data.full_name || '' }));
        if (Array.isArray(data.service_zips)) setZips(data.service_zips);
        setCredentials({
          drivers_license: data.drivers_license_path
            ? { path: data.drivers_license_path, filename: data.drivers_license_filename }
            : null,
          phlebotomy_cert: data.phlebotomy_cert_path
            ? { path: data.phlebotomy_cert_path, filename: data.phlebotomy_cert_filename }
            : null,
        });

        // Restore in-progress state. We have two sources:
        //   1. data.onboarding_progress — server-side autosave, follows the phleb across devices.
        //   2. localStorage — fast, but device-local.
        // Whichever has the newer savedAt wins. If neither has one, the DB row's
        // hydration above is the source of truth.
        let chosen = null;
        let chosenAt = 0;
        if (data.onboarding_progress) {
          const at = data.onboarding_progress.savedAt || 0;
          if (at >= chosenAt) { chosen = data.onboarding_progress; chosenAt = at; }
        }
        try {
          const raw = window.localStorage.getItem(`phleb-onboarding-${token}`);
          if (raw) {
            const saved = JSON.parse(raw);
            const at = saved.savedAt || 0;
            if (at > chosenAt) { chosen = saved; chosenAt = at; }
          }
        } catch (_) { /* corrupt local cache — ignore */ }

        if (chosen) {
          if (chosen.contact) setContact((c) => ({ ...c, ...chosen.contact }));
          if (chosen.w9) setW9((w) => ({ ...w, ...chosen.w9 }));
          if (chosen.agreement) {
            // per_draw_fee is admin-controlled. Always honor the live DB value,
            // not whatever was in the wizard cache at the time of last autosave.
            // The displayed value is phleb_pay_rate (phleb's take-home), with
            // lab_draw_fee as the fallback for pre-migration profiles.
            // eslint-disable-next-line no-unused-vars
            const { per_draw_fee: _ignoredFee, ...rest } = chosen.agreement;
            setAgreement((a) => ({
              ...a,
              ...rest,
              per_draw_fee: data.phleb_pay_rate != null
                ? String(data.phleb_pay_rate)
                : (data.lab_draw_fee != null ? String(data.lab_draw_fee) : ''),
            }));
          }
          if (Array.isArray(chosen.zips)) setZips(chosen.zips);
          if (Array.isArray(chosen.hours)) setHours(chosen.hours);
          if (typeof chosen.stepIdx === 'number' && chosen.stepIdx >= 0 && chosen.stepIdx < STEPS.length - 1) {
            setStepIdx(chosen.stepIdx);
          }
        }

        if (data.onboarding_status === 'completed') {
          setStepIdx(STEPS.length - 1);
        } else if (data.onboarding_status === 'pending') {
          await supabase
            .from('phlebotomist_profiles')
            .update({ onboarding_status: 'in_progress', onboarding_started_at: new Date().toISOString() })
            .eq('id', data.id);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const step = STEPS[stepIdx];
  const progress = ((stepIdx) / (STEPS.length - 1)) * 100;

  // Auto-save in-progress state to localStorage on every change for instant resume.
  // Credentials live in Supabase Storage already (saved on upload), so they're
  // restored from the DB on reload — no need to mirror their paths locally.
  useEffect(() => {
    if (loading || !phleb) return;
    if (phleb.onboarding_status === 'completed') return;
    try {
      window.localStorage.setItem(
        `phleb-onboarding-${token}`,
        JSON.stringify({ stepIdx, contact, w9, agreement, zips, hours, savedAt: Date.now() }),
      );
    } catch (_) { /* quota or private mode — ignore */ }
  }, [loading, phleb, token, stepIdx, contact, w9, agreement, zips, hours]);

  // Server-side autosave (debounced) so progress follows the phleb across devices.
  useEffect(() => {
    if (loading || !phleb) return;
    if (phleb.onboarding_status === 'completed') return;
    const timer = setTimeout(async () => {
      const payload = { stepIdx, contact, w9, agreement, zips, hours, savedAt: Date.now() };
      try {
        await supabase
          .from('phlebotomist_profiles')
          .update({
            onboarding_progress: payload,
            onboarding_progress_updated_at: new Date().toISOString(),
          })
          .eq('id', phleb.id);
      } catch (_) { /* network blip — localStorage still has the latest */ }
    }, 1500);
    return () => clearTimeout(timer);
  }, [loading, phleb, stepIdx, contact, w9, agreement, zips, hours]);

  const missingFields = () => {
    switch (step.id) {
      case 'welcome':
        return [
          !contact.full_name.trim() && { label: 'Full name', id: 'f-full-name' },
          !contact.email.trim() && { label: 'Email', id: 'f-email' },
        ].filter(Boolean);
      case 'contact':
        return [
          !contact.phone.trim() && { label: 'Phone', id: 'f-phone' },
          !contact.company_name.trim() && { label: 'Company name', id: 'f-company-name' },
          !contact.company_address.trim() && { label: 'Company address', id: 'f-company-address' },
        ].filter(Boolean);
      case 'credentials':
        return [
          !credentials.drivers_license?.path && { label: "Driver's license", id: 'f-drivers-license' },
          !credentials.phlebotomy_cert?.path && { label: 'Phlebotomy certification', id: 'f-phlebotomy-cert' },
        ].filter(Boolean);
      case 'w9':
        return [
          !w9.legal_name && { label: 'Legal name', id: 'f-w9-legal_name' },
          !w9.tax_classification && { label: 'Tax classification', id: 'f-w9-tax_classification' },
          !w9.address && { label: 'Address', id: 'f-w9-address' },
          !w9.city && { label: 'City', id: 'f-w9-city' },
          !w9.state && { label: 'State', id: 'f-w9-state' },
          !w9.zip && { label: 'ZIP', id: 'f-w9-zip' },
          (!w9.tin || w9.tin.length !== 9) && { label: `${w9.tin_type === 'ein' ? 'EIN' : 'SSN'} (9 digits)`, id: 'f-w9-tin' },
          !w9.certification && { label: 'Certification checkbox', id: 'f-w9-certification' },
          !w9.signature && { label: 'Signature', id: 'f-w9-signature' },
        ].filter(Boolean);
      case 'agreement':
        return [
          !agreement.company_name && { label: 'Company name', id: 'f-ag-company_name' },
          !agreement.printed_name && { label: 'Printed name', id: 'f-ag-printed_name' },
          !agreement.effective_date && { label: 'Effective date', id: 'f-ag-effective_date' },
          !(agreement.per_draw_fee && parseFloat(agreement.per_draw_fee) > 0) && { label: 'Per-draw fee not set (contact admin)', id: 'f-ag-per_draw_fee' },
          !agreement.bank_account_name && { label: 'Bank account name', id: 'f-ag-bank_account_name' },
          !agreement.bank_name && { label: 'Bank name', id: 'f-ag-bank_name' },
          !agreement.bank_routing_number && { label: 'Routing number', id: 'f-ag-bank_routing_number' },
          !agreement.bank_account_number && { label: 'Account number', id: 'f-ag-bank_account_number' },
          !agreement.compliance_acknowledged && { label: 'Compliance acknowledgement', id: 'f-ag-compliance' },
          !agreement.signature && { label: 'Signature', id: 'f-ag-signature' },
        ].filter(Boolean);
      case 'areas':
        return zips.length === 0 ? [{ label: 'Select at least one ZIP code', id: 'f-areas-list' }] : [];
      case 'password':
        return [
          (!password || password.length < 8) && { label: 'Password (min 8 characters)', id: 'f-password' },
          (password && password !== passwordConfirm) && { label: 'Confirm password must match', id: 'f-password-confirm' },
        ].filter(Boolean);
      default:
        return [];
    }
  };

  const canAdvance = () => missingFields().length === 0;

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Wait for the scroll, then focus an input (or a parent input if id is on a wrapper).
    setTimeout(() => {
      const focusable = el.matches('input,textarea,select,button') ? el : el.querySelector('input,textarea,select,button');
      focusable?.focus?.();
    }, 350);
  };

  const advance = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const finalize = async () => {
    if (!phleb) return;
    setSubmitting(true);
    try {
      const now = new Date().toISOString();

      // Generate the filled IRS W-9 PDF (flattened so it can't be re-edited)
      // and upload it to the credentials bucket alongside the other documents.
      let w9PdfPath = null;
      try {
        const sigBytes = w9.signature ? dataUrlToBytes(w9.signature) : null;
        const pdfBytes = await fillW9Pdf(w9, sigBytes, { flatten: true });
        const path = `${phleb.id}/w9-${Date.now()}.pdf`;
        const { error: upErr } = await supabase.storage
          .from('phleb-credentials')
          .upload(path, pdfBytes, { contentType: 'application/pdf', upsert: true });
        if (upErr) throw upErr;
        w9PdfPath = path;
      } catch (e) {
        // Non-fatal: we still save the structured W-9 data so admin can re-issue
        // the PDF later from src/utils/fillW9Pdf.js.
        // eslint-disable-next-line no-console
        console.error('Could not upload filled W-9 PDF:', e);
      }

      const { error: profileErr } = await supabase
        .from('phlebotomist_profiles')
        .update({
          full_name: contact.full_name,
          phone: contact.phone,
          company_name: contact.company_name,
          company_address: contact.company_address,
          // per_draw_fee shown in the wizard is the phleb's pay rate, not the
          // lab's bill rate. Save it back to phleb_pay_rate so admin's
          // lab_draw_fee (set in PhlebotomistManagement) is not overwritten.
          phleb_pay_rate: parseFloat(agreement.per_draw_fee),
          service_zips: zips,
          drivers_license_path: credentials.drivers_license?.path || null,
          drivers_license_filename: credentials.drivers_license?.filename || null,
          drivers_license_uploaded_at: credentials.drivers_license ? now : null,
          phlebotomy_cert_path: credentials.phlebotomy_cert?.path || null,
          phlebotomy_cert_filename: credentials.phlebotomy_cert?.filename || null,
          phlebotomy_cert_uploaded_at: credentials.phlebotomy_cert ? now : null,
          w9_data: {
            legal_name: w9.legal_name,
            business_name: w9.business_name || null,
            tax_classification: w9.tax_classification,
            llc_classification: w9.llc_classification || null,
            other_classification: w9.other_classification || null,
            address: w9.address,
            address2: w9.address2 || null,
            city: w9.city,
            state: w9.state,
            zip: w9.zip,
            tin_type: w9.tin_type || 'ssn',
            tin: w9.tin,
            certification: w9.certification,
          },
          w9_signature: w9.signature,
          w9_signed_at: now,
          w9_pdf_path: w9PdfPath,
          onboarding_progress: null,
          agreement_signed: true,
          agreement_signed_at: now,
          agreement_company_name: agreement.company_name,
          agreement_printed_name: agreement.printed_name,
          agreement_date: agreement.sign_date,
          agreement_signature: agreement.signature,
          onboarding_status: 'completed',
          onboarding_completed_at: now,
          updated_at: now,
        })
        .eq('id', phleb.id);
      if (profileErr) throw profileErr;

      // Agreement history (mirrors AgreementSign behavior so existing admin views keep working).
      const { data: agreementHistory, error: aghErr } = await supabase
        .from('phlebotomist_agreement_history')
        .insert({
          phlebotomist_id: phleb.id,
          agreement_version: 'v1.0',
          is_current: true,
          agreement_signed: true,
          agreement_signed_at: now,
          agreement_effective_date: agreement.effective_date,
          agreement_termination_notice_days: 30,
          agreement_company_name: agreement.company_name,
          agreement_printed_name: agreement.printed_name,
          agreement_signature: agreement.signature,
          per_draw_fee: parseFloat(agreement.per_draw_fee),
          payment_frequency: 'monthly',
          payment_method: 'ACH',
          compliance_acknowledged: agreement.compliance_acknowledged,
          compliance_acknowledged_at: now,
          baa_signed: false,
          fmv_certified: false,
        })
        .select()
        .single();
      if (aghErr) throw aghErr;

      // Mark prior agreements replaced (RPC exists in current schema).
      if (agreementHistory?.id) {
        await supabase.rpc('mark_previous_agreement_replaced', {
          p_phlebotomist_id: phleb.id,
          p_new_agreement_id: agreementHistory.id,
        });
      }

      await supabase.from('phlebotomist_payment_info_history').insert({
        phlebotomist_id: phleb.id,
        agreement_history_id: agreementHistory.id,
        is_current: true,
        effective_date: agreement.effective_date,
        bank_account_name: agreement.bank_account_name,
        bank_name: agreement.bank_name,
        bank_routing_number: agreement.bank_routing_number,
        bank_account_number: agreement.bank_account_number,
        bank_account_type: agreement.bank_account_type,
        payment_method: 'ACH',
        payment_frequency: 'monthly',
        bank_verified: false,
      });

      // Working hours: clear existing for this phleb, insert selected.
      await supabase.from('working_hours').delete().eq('phlebotomist_id', phleb.id);
      const hoursRows = hours
        .filter((h) => h.available)
        .map((h) => ({
          phlebotomist_id: phleb.id,
          day_of_week: h.day_of_week,
          start_time: h.start_time,
          end_time: h.end_time,
        }));
      if (hoursRows.length > 0) {
        await supabase.from('working_hours').insert(hoursRows);
      }

      // (Password is set below via /api/set-phleb-password — no reset email needed.)

      // Set the phleb's auth password via our token-gated API so they can sign
      // in immediately without a separate password-reset email.
      try {
        const resp = await fetch('/api/set-phleb-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, password }),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.error || `Password set failed (${resp.status})`);
        }
      } catch (e) {
        toast({
          title: 'Could not set your password',
          description: `${e.message}. We'll email you a setup link instead.`,
          status: 'warning',
          duration: 6000,
        });
        // Fallback: still send reset email so they have a way to sign in.
        await supabase.auth.resetPasswordForEmail(contact.email, {
          redirectTo: `${window.location.origin}/phlebotomist/login`,
        }).catch(() => {});
      }

      try { window.localStorage.removeItem(`phleb-onboarding-${token}`); } catch (_) { /* ignore */ }
      toast({ title: 'Onboarding complete!', status: 'success', duration: 4000, isClosable: true });
      setStepIdx(STEPS.length - 1);
    } catch (e) {
      toast({ title: 'Could not complete onboarding', description: e.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Flex h="80vh" align="center" justify="center"><Spinner /></Flex>;
  }
  if (error) {
    return (
      <Flex h="80vh" align="center" justify="center">
        <Alert status="error" maxW="md">
          <AlertIcon />{error}
        </Alert>
      </Flex>
    );
  }

  return (
    <Box maxW="900px" mx="auto" mt={8} p={6} bg="white" borderRadius="xl" boxShadow="md">
      <VStack align="stretch" spacing={5}>
        {/* Identity strip — visible on every step so the phleb always knows whose
            onboarding they're filling out (helps when admins test multiple links). */}
        {(contact.full_name || contact.email) && (
          <Flex
            align="center"
            justify="space-between"
            bg="blue.50"
            borderWidth="1px"
            borderColor="blue.100"
            borderRadius="lg"
            px={4}
            py={2.5}
            flexWrap="wrap"
            gap={2}
          >
            <HStack spacing={3}>
              <Flex align="center" justify="center" bg="blue.500" color="white" boxSize="36px" borderRadius="full" fontWeight="700" fontSize="sm">
                {(contact.full_name || contact.email || '?')
                  .split(/[\s@.]+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((s) => s[0].toUpperCase())
                  .join('') || '?'}
              </Flex>
              <Box>
                <Text fontSize="2xs" color="blue.700" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
                  Onboarding for
                </Text>
                <Text fontSize="sm" fontWeight="700" color="gray.800" lineHeight="1.2">
                  {contact.full_name || 'New phlebotomist'}
                </Text>
                {contact.email && (
                  <Text fontSize="xs" color="gray.600">{contact.email}</Text>
                )}
              </Box>
            </HStack>
            <Text fontSize="2xs" color="blue.700" fontWeight="600">
              At Home Blood Draw · Phlebotomist Portal
            </Text>
          </Flex>
        )}

        <Box>
          <Flex justify="space-between" align="center">
            <Heading size="md">Phlebotomist Onboarding</Heading>
            {step.id !== 'welcome' && step.id !== 'done' && (
              <Text fontSize="xs" color="gray.400">Progress saved — resume from any device</Text>
            )}
          </Flex>
          <Text fontSize="sm" color="gray.500" mt={1}>Step {stepIdx + 1} of {STEPS.length}: {step.title}</Text>
          <Progress value={progress} size="xs" mt={3} colorScheme="blue" borderRadius="full" />
        </Box>

        {step.id === 'welcome' && (
          <VStack align="stretch" spacing={6}>
            {/* Brand hero */}
            <Box
              bgGradient="linear(to-br, blue.600, blue.500)"
              color="white"
              borderRadius="xl"
              p={{ base: 5, md: 7 }}
            >
              <HStack spacing={4} align="center" mb={3}>
                <Image src="/logo.png" alt="At Home Blood Draw" boxSize="56px" borderRadius="md" bg="white" p={1} fallback={<Box boxSize="56px" />} />
                <Box>
                  <Text fontSize="xs" fontWeight="600" letterSpacing="0.1em" opacity={0.85} textTransform="uppercase">At Home Blood Draw</Text>
                  <Heading size="md" lineHeight="1.2">Welcome aboard{contact.full_name ? `, ${contact.full_name.split(' ')[0]}` : ''}!</Heading>
                </Box>
              </HStack>
              <Text fontSize="sm" opacity={0.95} lineHeight="1.6">
                We connect patients, doctors, and labs with skilled mobile phlebotomists like you.
                Our team has set up your profile — the next few steps get you ready to start
                accepting blood draws in your service area.
              </Text>
            </Box>

            {/* What to expect */}
            <Box>
              <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em" mb={3}>
                What you'll do — about 10 minutes
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {[
                  { icon: FaUserCheck,     title: 'Confirm contact & company',     desc: 'Verify name, email, phone, and your business details.' },
                  { icon: FaShieldAlt,     title: 'Upload credentials',            desc: "Driver's license + phlebotomy certification for our records." },
                  { icon: FaIdCard,        title: 'IRS Form W-9',                  desc: 'Standard tax form so we can pay you as a 1099 contractor.' },
                  { icon: FaFileSignature, title: 'Service agreement',             desc: 'Sign our independent-contractor agreement and add ACH details.' },
                  { icon: FaMapMarkedAlt,  title: 'Service areas',                 desc: 'Click NYC ZIP codes on a map to mark where you can draw.' },
                  { icon: FaCalendarAlt,   title: 'Weekly availability',           desc: 'Tell us which days and hours you can work.' },
                  { icon: FaKey,           title: 'Set your password',             desc: 'Choose a sign-in password so you can access your dashboard.' },
                  { icon: FaHandshake,     title: "You're live",                   desc: 'Sign in to your dashboard and start picking up draws.' },
                ].map((step, i) => (
                  <HStack key={i} align="start" spacing={3} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100">
                    <Flex align="center" justify="center" bg="blue.50" color="blue.600" borderRadius="md" boxSize="36px" flexShrink={0}>
                      <Icon as={step.icon} boxSize={4} />
                    </Flex>
                    <Box>
                      <Text fontSize="sm" fontWeight="600" color="gray.800">{i + 1}. {step.title}</Text>
                      <Text fontSize="xs" color="gray.600" lineHeight="1.5">{step.desc}</Text>
                    </Box>
                  </HStack>
                ))}
              </SimpleGrid>
            </Box>

            {/* How the work happens */}
            <Box borderWidth="1px" borderColor="blue.100" bg="blue.50" borderRadius="lg" p={4}>
              <HStack spacing={3} align="start">
                <Icon as={FaRoute} color="blue.600" boxSize={5} mt={0.5} />
                <Box>
                  <Text fontSize="sm" fontWeight="700" color="blue.800" mb={1}>How draws work after you're set up</Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                    Once onboarding is complete, you'll see a dashboard with every new draw and
                    patient request that comes in. Draws are filtered to your selected NYC ZIP codes,
                    so you only see what's near you. Pick up the ones that fit your schedule, route them
                    with the built-in map, complete the draw, and we handle the lab handoff and your monthly ACH payment.
                  </Text>
                </Box>
              </HStack>
            </Box>

            <Divider />

            {/* Confirm details */}
            <Box>
              <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em" mb={2}>
                Confirm your details
              </Text>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Your name and email were set up by our team. Update them here if anything's wrong.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Full name</FormLabel>
                  <Input id="f-full-name" size="sm" value={contact.full_name} onChange={(e) => setContact({ ...contact, full_name: e.target.value })} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Email</FormLabel>
                  <Input id="f-email" size="sm" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                </FormControl>
              </SimpleGrid>
            </Box>
          </VStack>
        )}

        {step.id === 'contact' && (
          <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Phone</FormLabel>
                <Input id="f-phone" size="sm" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Company name</FormLabel>
                <Input id="f-company-name" size="sm" value={contact.company_name} onChange={(e) => setContact({ ...contact, company_name: e.target.value })} />
              </FormControl>
            </SimpleGrid>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Company address</FormLabel>
              <Textarea id="f-company-address" size="sm" rows={2} value={contact.company_address} onChange={(e) => setContact({ ...contact, company_address: e.target.value })} />
            </FormControl>
          </VStack>
        )}

        {step.id === 'credentials' && (
          <Box id="f-credentials-wrapper">
            <Box id="f-drivers-license" />
            <Box id="f-phlebotomy-cert" />
            <CredentialsUpload
              phlebId={phleb.id}
              value={credentials}
              onChange={setCredentials}
            />
          </Box>
        )}

        {step.id === 'w9' && <W9Form value={w9} onChange={setW9} />}

        {step.id === 'agreement' && (
          <VStack align="stretch" spacing={4}>
            <Box maxH="260px" overflowY="auto" p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
              <Text fontSize="xs" whiteSpace="pre-wrap" lineHeight="1.7" color="gray.700">
                {DEFAULT_AGREEMENT
                  .replace('{PER_DRAW_FEE}', agreement.per_draw_fee ? `$${parseFloat(agreement.per_draw_fee).toFixed(2)}` : '$____')
                  .replace('{EFFECTIVE_DATE}', agreement.effective_date || '____')}
              </Text>
            </Box>

            {/* Compensation callout — fee comes from admin and is locked. */}
            <Box id="f-ag-per_draw_fee" borderWidth="1px" borderColor="green.200" bg="green.50" borderRadius="lg" p={4}>
              <Text fontSize="2xs" fontWeight="700" color="green.700" textTransform="uppercase" letterSpacing="0.05em" mb={2}>
                Compensation
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="600">Per Draw</Text>
                  <Text fontSize="2xl" fontWeight="700" color="green.700">
                    {agreement.per_draw_fee
                      ? `$${parseFloat(agreement.per_draw_fee).toFixed(2)}`
                      : 'Not set'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="600">Payment Terms</Text>
                  <Text fontSize="sm" color="gray.700">Biweekly invoice, paid within 15 days</Text>
                  <Text fontSize="xs" color="gray.500" mt={0.5}>$10 late fee applies</Text>
                </Box>
              </SimpleGrid>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Set by our team and locked into this agreement. Reach out if anything looks wrong.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Company name</FormLabel>
                <Input id="f-ag-company_name" size="sm" value={agreement.company_name} onChange={(e) => setAgreement({ ...agreement, company_name: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Printed name</FormLabel>
                <Input id="f-ag-printed_name" size="sm" value={agreement.printed_name} onChange={(e) => setAgreement({ ...agreement, printed_name: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Effective date</FormLabel>
                <Input id="f-ag-effective_date" size="sm" type="date" value={agreement.effective_date} onChange={(e) => setAgreement({ ...agreement, effective_date: e.target.value })} />
              </FormControl>
            </SimpleGrid>

            <Divider />
            <Heading size="xs" color="gray.600">Bank info (ACH)</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Account name</FormLabel>
                <Input id="f-ag-bank_account_name" size="sm" value={agreement.bank_account_name} onChange={(e) => setAgreement({ ...agreement, bank_account_name: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Bank name</FormLabel>
                <Input id="f-ag-bank_name" size="sm" value={agreement.bank_name} onChange={(e) => setAgreement({ ...agreement, bank_name: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Routing number</FormLabel>
                <Input id="f-ag-bank_routing_number" size="sm" value={agreement.bank_routing_number} onChange={(e) => setAgreement({ ...agreement, bank_routing_number: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Account number</FormLabel>
                <Input id="f-ag-bank_account_number" size="sm" type="password" value={agreement.bank_account_number} onChange={(e) => setAgreement({ ...agreement, bank_account_number: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Account type</FormLabel>
                <Select size="sm" value={agreement.bank_account_type} onChange={(e) => setAgreement({ ...agreement, bank_account_type: e.target.value })}>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </Select>
              </FormControl>
            </SimpleGrid>

            <Checkbox
              id="f-ag-compliance"
              isChecked={agreement.compliance_acknowledged}
              onChange={(e) => setAgreement({ ...agreement, compliance_acknowledged: e.target.checked })}
              colorScheme="blue"
            >
              I acknowledge compliance with all applicable federal and state laws (Anti-Kickback, Stark, HIPAA).
            </Checkbox>

            <Box id="f-ag-signature">
              <FormLabel fontSize="sm">Signature</FormLabel>
              <Box borderWidth="2px" borderRadius="md" p={2} bg="white">
                <SignatureCanvas
                  penColor="black"
                  canvasProps={{ width: 500, height: 100 }}
                  ref={agreementSigPad}
                  onEnd={() => {
                    const dataUrl = agreementSigPad.current.getCanvas().toDataURL('image/png');
                    setAgreement((a) => ({ ...a, signature: dataUrl }));
                  }}
                />
              </Box>
              <Button size="xs" mt={2} onClick={() => { agreementSigPad.current?.clear(); setAgreement((a) => ({ ...a, signature: null })); }}>
                Clear
              </Button>
            </Box>
          </VStack>
        )}

        {step.id === 'areas' && (
          <Box id="f-areas-list"><ServiceZipPicker value={zips} onChange={setZips} /></Box>
        )}

        {step.id === 'hours' && (
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm" color="gray.600">Set the hours you're available each day. Uncheck days you don't work.</Text>
            {hours.map((h, i) => (
              <HStack key={h.day_of_week} spacing={4} align="center">
                <Checkbox
                  isChecked={h.available}
                  onChange={(e) => {
                    const next = [...hours];
                    next[i] = { ...next[i], available: e.target.checked };
                    setHours(next);
                  }}
                  w="120px"
                >
                  {DAYS[h.day_of_week]}
                </Checkbox>
                <Input
                  size="sm" type="time" w="130px"
                  value={h.start_time}
                  isDisabled={!h.available}
                  onChange={(e) => {
                    const next = [...hours];
                    next[i] = { ...next[i], start_time: e.target.value };
                    setHours(next);
                  }}
                />
                <Text fontSize="sm" color="gray.500">to</Text>
                <Input
                  size="sm" type="time" w="130px"
                  value={h.end_time}
                  isDisabled={!h.available}
                  onChange={(e) => {
                    const next = [...hours];
                    next[i] = { ...next[i], end_time: e.target.value };
                    setHours(next);
                  }}
                />
              </HStack>
            ))}
          </VStack>
        )}

        {step.id === 'password' && (
          <VStack align="stretch" spacing={4}>
            <Box bg="blue.50" borderWidth="1px" borderColor="blue.200" borderRadius="lg" p={4}>
              <Heading size="sm" color="blue.800" mb={2}>Set your sign-in password</Heading>
              <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                You'll use this password (with <b>{contact.email}</b>) to sign in to your
                dashboard and see draws. Choose something you'll remember — at least 8 characters.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Password</FormLabel>
                <Input
                  id="f-password"
                  size="sm"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Confirm password</FormLabel>
                <Input
                  id="f-password-confirm"
                  size="sm"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                />
              </FormControl>
            </SimpleGrid>
            <Checkbox
              size="sm"
              isChecked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
            >
              Show password
            </Checkbox>
            {password && password.length < 8 && (
              <Text fontSize="xs" color="orange.600">Password must be at least 8 characters.</Text>
            )}
            {password && passwordConfirm && password !== passwordConfirm && (
              <Text fontSize="xs" color="red.500">Passwords don't match.</Text>
            )}
          </VStack>
        )}

        {step.id === 'done' && (
          <VStack align="stretch" spacing={4} textAlign="center" py={6}>
            <Box color="green.500" fontSize="5xl">✓</Box>
            <Heading size="md" color="green.600">You're all set, {contact.full_name}!</Heading>
            <Text fontSize="sm" color="gray.600">
              We've recorded your W-9, signed agreement, service areas, and availability.
              Sign in any time at our portal using <b>{contact.email}</b> and the password
              you just set, and you'll see all draws and patients in your service area.
            </Text>
            <HStack justify="center" spacing={3}>
              <Badge colorScheme="green">W-9 signed</Badge>
              <Badge colorScheme="green">Agreement signed</Badge>
              <Badge colorScheme="blue">{zips.length} ZIP codes</Badge>
              <Badge colorScheme="purple">{hours.filter((h) => h.available).length} days available</Badge>
            </HStack>
            <Button colorScheme="blue" onClick={() => navigate('/phlebotomist/login')}>
              Go to Sign In
            </Button>
          </VStack>
        )}

        {step.id !== 'done' && (() => {
          const missing = missingFields();
          const isLast = stepIdx >= STEPS.length - 2;
          return (
            <VStack align="stretch" spacing={2} pt={2}>
              <Stack direction="row" justify="space-between">
                <Button variant="ghost" onClick={back} isDisabled={stepIdx === 0}>Back</Button>
                {!isLast ? (
                  <Button colorScheme="blue" onClick={advance} isDisabled={missing.length > 0}>Next</Button>
                ) : (
                  <Button
                    colorScheme="green"
                    onClick={finalize}
                    isDisabled={missing.length > 0 || submitting}
                    isLoading={submitting}
                    loadingText="Saving…"
                  >
                    Finish Onboarding
                  </Button>
                )}
              </Stack>
              {missing.length > 0 && (
                <Box bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="md" p={3}>
                  <Text fontSize="xs" fontWeight="700" color="orange.700" mb={2} textTransform="uppercase" letterSpacing="0.05em">
                    Before you can continue — {missing.length} thing{missing.length > 1 ? 's' : ''} left
                  </Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {missing.map((m) => (
                      <Button
                        key={m.id}
                        size="xs"
                        variant="outline"
                        colorScheme="orange"
                        borderRadius="full"
                        onClick={() => jumpTo(m.id)}
                      >
                        {m.label} →
                      </Button>
                    ))}
                  </HStack>
                </Box>
              )}
            </VStack>
          );
        })()}
      </VStack>
    </Box>
  );
}
